import StatusIndicator from '@risk-smart/themed-cloudscape-components/status-indicator';
import type { FC } from 'react';

import type { AgentMetadata } from './useChatStore';

type ToolCall = NonNullable<AgentMetadata['toolCalls']>[number];

const statusFor = (
  toolCall: ToolCall
): { type: 'loading' | 'success' | 'error'; label: string } => {
  if (!toolCall.endTime) {
    return { type: 'loading', label: `${toolCall.toolName} — running` };
  }

  return toolCall.success === false
    ? { type: 'error', label: `${toolCall.toolName} — failed` }
    : { type: 'success', label: toolCall.toolName };
};

/** Truncated, readable peek at a tool's input/result for the expandable body. */
const peek = (value: unknown, max = 600): string | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const text =
    typeof value === 'string' ? value : JSON.stringify(value, null, 2);

  return text.length > max ? `${text.slice(0, max)}…` : text;
};

/**
 * Renders the read-only Agent's tool calls beneath a message: each tool's name +
 * live status (running / done / failed), with an expandable peek at the input
 * args and result. Fed from the chat store's `toolCalls` metadata (populated by
 * the gateway adaptor) — used only for agent-mode messages.
 */
export const ToolCallRenderer: FC<{ toolCalls: ToolCall[] }> = ({
  toolCalls,
}) => {
  if (toolCalls.length === 0) {
    return null;
  }

  return (
    <div className={'mt-2 flex flex-col gap-1'} data-testid={'tool-calls'}>
      {toolCalls.map((toolCall, index) => {
        const status = statusFor(toolCall);
        const args = peek(toolCall.toolArgs);
        const result = peek(toolCall.result);

        return (
          <details
            key={
              toolCall.toolUseId ??
              `${toolCall.toolName}-${toolCall.startTime}-${index}`
            }
            className={'text-sm'}
            data-testid={'tool-call'}
          >
            <summary className={'cursor-pointer'}>
              <StatusIndicator type={status.type}>
                {status.label}
              </StatusIndicator>
            </summary>
            {args !== null && (
              <div className={'mt-1'}>
                <strong>{'Input'}</strong>
                <pre className={'overflow-x-auto whitespace-pre-wrap text-xs'}>
                  {args}
                </pre>
              </div>
            )}
            {result !== null && (
              <div className={'mt-1'}>
                <strong>{'Result'}</strong>
                <pre className={'overflow-x-auto whitespace-pre-wrap text-xs'}>
                  {result}
                </pre>
              </div>
            )}
          </details>
        );
      })}
    </div>
  );
};
